import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CustomDropdown,
  Modal,
  TextField,
  fieldLabelClasses,
} from "../../../components/ui";
import { useMealPlanTypes } from "../hooks/useMealPlanTypes";

/** What a copy is called before anything is typed over it. */
const copyName = (name: string) => `${name.trim()} (Copy)`.slice(0, 80);

export interface DuplicateMealPlanModalProps {
  /**
   * The plan being copied. Only the two things the dialog has to fill itself
   * in from: the name the copy's name is suggested off, and the kind of plan
   * it is, which the dropdown opens on.
   */
  plan: { name: string; planType: string };
  onClose: () => void;
  /**
   * Given the finished answers — making the copy is the caller's to do, and it
   * is a request, so this may be awaited. The dialog stays open and usable
   * until it resolves, then the caller closes it.
   */
  onDuplicate: (input: {
    name: string;
    planTypeId: number;
  }) => void | Promise<void>;
}

/**
 * The Duplicate meal plan dialog: what the copy is called, and what kind of
 * plan it is.
 *
 * One step rather than the create dialog's two — a copy is for whoever the
 * plan it came from was written for, so there is no client to ask about.
 *
 * Mounted only while it is open, so the plan types are fetched when the dialog
 * opens rather than with the table behind it, and every answer is forgotten on
 * close instead of being left over for the next copy.
 */
const DuplicateMealPlanModal: React.FC<DuplicateMealPlanModalProps> = ({
  plan,
  onClose,
  onDuplicate,
}) => {
  const [name, setName] = useState(() => copyName(plan.name));
  const [planTypeId, setPlanTypeId] = useState<number | null>(null);
  // Duplicating is a request, and a second one would make a second plan.
  const [saving, setSaving] = useState(false);

  const { options, status } = useMealPlanTypes();

  const rows = useMemo(
    () => options.map((type) => ({ value: String(type.id), label: type.name })),
    [options]
  );

  // The kind of plan the copy is opens on the kind the original was — that is
  // what a duplicate is, and changing it is the exception rather than the
  // answer. A plan carries its type as a name and the create takes an id, so
  // the catalog is what turns one into the other; a type this workspace no
  // longer offers matches nothing and leaves the field to be answered.
  //
  // Only while it has not been answered: this runs again when the catalog is
  // refetched in the background, and a choice already made is not the
  // original's to overwrite.
  useEffect(() => {
    if (planTypeId != null) return;

    const wanted = plan.planType.trim().toLowerCase();
    const match = options.find(
      (type) => type.name.trim().toLowerCase() === wanted
    );
    if (match) setPlanTypeId(match.id);
  }, [options, plan.planType, planTypeId]);

  const placeholder =
    status === "loading"
      ? "Loading plan types…"
      : status === "failed"
      ? "Could not load plan types"
      : "Select plan type";

  const handleDuplicate = async () => {
    if (planTypeId == null || !name.trim() || saving) return;

    setSaving(true);
    try {
      await onDuplicate({ name: name.trim(), planTypeId });
    } finally {
      // The dialog is unmounted by the caller on success, so this only ever
      // runs after a failure — which is exactly when it has to be usable again.
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Duplicate meal plan"
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="md"
            disabled={!name.trim() || planTypeId == null || saving}
            onClick={handleDuplicate}
          >
            {saving ? "Duplicating…" : "Duplicate & open"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label="New plan name"
          value={name}
          onValueChange={setName}
          placeholder="Untitle plan"
          maxLength={80}
          autoFocus
        />
        <div>
          <label
            id="duplicate-plan-type-label"
            className={`${fieldLabelClasses} mb-1.5`}
          >
            Select plan
          </label>
          {/* `CustomDropdown` rather than `DropdownField`, for the same reason
              the create dialog uses one: the row has to carry the id, and the
              labelled field only deals in strings. The duplicate sends
              `plan_type: 1`, not `plan_type: "Regular"`. */}
          <CustomDropdown
            options={rows}
            value={planTypeId == null ? "" : String(planTypeId)}
            onChange={(value) => setPlanTypeId(Number(value))}
            placeholder={placeholder}
            disabled={status !== "ready"}
            searchable
            aria-labelledby="duplicate-plan-type-label"
          />
        </div>
      </div>
    </Modal>
  );
};

export default DuplicateMealPlanModal;
